import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';

import {of} from 'rxjs';
import {catchError, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {DocumentsService, SignatureMethodDto} from '../../../shared/documents/documents.service';
import {
  getAvailableSignatureMethods,
  sortByOptionOrder
} from '../../../shared/documents/signature/signature-method.util';
import {SignatureDataEntry, SignatureMethodEnum} from '../../../shared/files/files.model';
import {SignatureRoleEntry} from '../../template-management.interfaces';
import {
  decodeSignatureData,
  encodeSignatureData,
  fingerprintOfDataUri,
  MAX_SIGNATURE_ROLE_LENGTH,
  MAX_SIGNATURE_ROLES,
  resolveSignatureIndexByFingerprints,
  SIGNATURE_ASSETS,
  stampRoleOnSignature
} from '../signature-image.util';

@Component({
  selector: 'gp-template-signature-panel',
  templateUrl: './template-signature-panel.component.html',
  styleUrls: ['./template-signature-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TemplateSignaturePanelComponent extends Destroyable implements OnInit, OnChanges {

  @ViewChild('signerRoleInput')
  public signerRoleInput?: ElementRef<HTMLInputElement>;

  @Input()
  public signatureData: SignatureDataEntry[] = [];

  @Input()
  public cliRegId!: number;

  @Input()
  public countSignaturesInDocument?: (fingerprints: string[]) => number;

  @Input()
  public documentChangeCounter = 0;

  @Output()
  public insertSignature: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public removeSignatures: EventEmitter<string[]> = new EventEmitter<string[]>();

  @Output()
  public signatureDataChange: EventEmitter<SignatureDataEntry[]> = new EventEmitter<SignatureDataEntry[]>();

  @Output()
  public closePanel: EventEmitter<void> = new EventEmitter<void>();

  public SignatureMethodEnum = SignatureMethodEnum;
  public signerRoleControl = new UntypedFormControl('');
  public signatureRoleEntries: SignatureRoleEntry[] = [];
  public signatureMethodOptions: SignatureMethodEnum[] = [SignatureMethodEnum.MANUAL];
  public selectedSignatureMethods: SignatureMethodEnum[] = [SignatureMethodEnum.MANUAL];
  public signatureOrderMode: 'UNORDERED' | 'ORDERED' = 'UNORDERED';
  public readonly maxSignatureRoles = MAX_SIGNATURE_ROLES;
  public readonly maxSignatureRoleLength = MAX_SIGNATURE_ROLE_LENGTH;
  public draggedSignerRoleIndex: number | null = null;
  public pendingDeletionRole: string | null = null;
  private hasStoredSignatureMethods = false;
  private signatureMethodOptionsLoaded = false;
  private signatureIndexesByRole = new Map<string, number>();

  constructor(private cd: ChangeDetectorRef,
              private documentsService: DocumentsService) {
    super();
  }

  public get canAddSignerRole(): boolean {
    const role = (this.signerRoleControl.value ?? '').trim();
    return role.length > 0
      && role.length <= MAX_SIGNATURE_ROLE_LENGTH
      && this.signatureRoleEntries.length < MAX_SIGNATURE_ROLES
      && this.signatureMethodOptions.length > 0
      && !this.signatureRoleEntries.some((entry) => entry.role.toLowerCase() === role.toLowerCase());
  }

  public get canConfigureSigningOrder(): boolean {
    return this.signatureRoleEntries.length >= 2;
  }

  public ngOnInit(): void {
    this.signerRoleControl.setValue('');
    this.syncSignatureRoleEntries();
    this.loadSignatureMethodOptions();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.signatureData) {
      this.syncSignatureRoleEntries();
    }
    if (changes?.cliRegId && !changes.cliRegId.firstChange) {
      this.loadSignatureMethodOptions();
    }
  }

  public close(): void {
    this.closePanel.emit();
  }

  public addSignerRole(): void {
    if (!this.canAddSignerRole) {
      return;
    }
    const role = (this.signerRoleControl.value ?? '').trim();
    const signatureIndex = this.nextAvailableSignatureIndex();
    stampRoleOnSignature(SIGNATURE_ASSETS[signatureIndex], role)
      .then((imageDataUri: string) => {
        const entry: SignatureRoleEntry = {
          role,
          signatureIndex,
          imageDataUri,
          fingerprints: [fingerprintOfDataUri(imageDataUri)],
          signatureMethods: [...this.selectedSignatureMethods]
        };
        this.signatureRoleEntries = [...this.signatureRoleEntries, entry];
        this.signatureIndexesByRole.set(role, signatureIndex);
        this.signerRoleControl.setValue('');
        this.emitSignatureData();
        this.cd.markForCheck();
        this.signerRoleInput?.nativeElement.focus();
      });
  }

  public addSignatureForRole(entry: SignatureRoleEntry): void {
    const insert = (imageDataUri: string): void => {
      entry.imageDataUri = imageDataUri;
      const fingerprint = fingerprintOfDataUri(imageDataUri);
      if (fingerprint && !entry.fingerprints.includes(fingerprint)) {
        entry.fingerprints = [...entry.fingerprints, fingerprint];
        this.emitSignatureData();
      }
      this.insertSignature.emit(imageDataUri);
      this.cd.markForCheck();
    };

    if (entry.imageDataUri) {
      insert(entry.imageDataUri);
      return;
    }
    stampRoleOnSignature(SIGNATURE_ASSETS[entry.signatureIndex], entry.role).then(insert);
  }

  public requestDeleteSignerRole(entry: SignatureRoleEntry): void {
    this.pendingDeletionRole = entry.role;
  }

  public cancelDeleteSignerRole(): void {
    this.pendingDeletionRole = null;
  }

  public deleteSignerRole(entry: SignatureRoleEntry): void {
    this.removeSignatures.emit(entry.fingerprints);
    this.signatureIndexesByRole.delete(entry.role);
    this.signatureRoleEntries = this.signatureRoleEntries.filter((item) => item.role !== entry.role);
    this.pendingDeletionRole = null;
    this.normalizeSignatureOrderMode();
    this.emitSignatureData();
    this.cd.markForCheck();
  }

  public selectSignatureMethod(method: SignatureMethodEnum): void {
    if (!this.signatureRoleEntries.length || !this.signatureMethodOptions.includes(method)) {
      return;
    }
    if (this.selectedSignatureMethods.includes(method)) {
      if (this.selectedSignatureMethods.length === 1) {
        return;
      }
      this.selectedSignatureMethods = this.selectedSignatureMethods.filter((selected) => selected !== method);
    } else {
      this.selectedSignatureMethods = sortByOptionOrder(
        this.signatureMethodOptions,
        [...this.selectedSignatureMethods, method]
      );
    }
    this.applySignatureMethodsToAllEntries();
    this.emitSignatureData();
    this.cd.markForCheck();
  }

  public signatureAssetFor(entry: SignatureRoleEntry): string {
    return SIGNATURE_ASSETS[entry?.signatureIndex] ?? SIGNATURE_ASSETS[0];
  }

  public signatureCountFor(entry: SignatureRoleEntry): number {
    return this.countSignaturesInDocument?.(entry.fingerprints) ?? 0;
  }

  public changeSignatureOrderMode(ordered: boolean): void {
    this.signatureOrderMode = ordered && this.canConfigureSigningOrder ? 'ORDERED' : 'UNORDERED';
    this.emitSignatureData();
  }

  public changeSignatureOrder(event: Event): void {
    this.changeSignatureOrderMode((event.target as HTMLInputElement).checked);
  }

  public startDraggingSignerRole(index: number, event: DragEvent): void {
    if (this.signatureOrderMode !== 'ORDERED') {
      event.preventDefault();
      return;
    }
    this.draggedSignerRoleIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
    }
  }

  public allowSignerRoleDrop(event: DragEvent): void {
    if (this.signatureOrderMode !== 'ORDERED') {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  public dropSignerRole(index: number, event: DragEvent): void {
    event.preventDefault();
    if (this.signatureOrderMode !== 'ORDERED'
      || this.draggedSignerRoleIndex === null
      || this.draggedSignerRoleIndex === index) {
      this.draggedSignerRoleIndex = null;
      return;
    }
    const entries = [...this.signatureRoleEntries];
    const [draggedEntry] = entries.splice(this.draggedSignerRoleIndex, 1);
    entries.splice(index, 0, draggedEntry);
    this.signatureRoleEntries = entries;
    this.draggedSignerRoleIndex = null;
    this.emitSignatureData();
    this.cd.markForCheck();
  }

  public stopDraggingSignerRole(): void {
    this.draggedSignerRoleIndex = null;
  }

  public trackBySignerRole(_index: number, entry: SignatureRoleEntry): string {
    return entry.role;
  }

  private moveSignerRole(fromIndex: number, toIndex: number): void {
    if (fromIndex < 0 || fromIndex >= this.signatureRoleEntries.length
      || toIndex < 0 || toIndex >= this.signatureRoleEntries.length) {
      return;
    }
    const entries = [...this.signatureRoleEntries];
    const [entry] = entries.splice(fromIndex, 1);
    entries.splice(toIndex, 0, entry);
    this.signatureRoleEntries = entries;
    this.emitSignatureData();
    this.cd.markForCheck();
  }

  private applySignatureMethodsToAllEntries(): void {
    this.signatureRoleEntries = this.signatureRoleEntries.map((entry) => ({
      ...entry,
      signatureMethods: [...this.selectedSignatureMethods]
    }));
  }

  private initSelectedSignatureMethods(): void {
    this.signatureMethodOptionsLoaded = true;
    const available = sortByOptionOrder(this.signatureMethodOptions, this.selectedSignatureMethods);
    this.selectedSignatureMethods = this.hasStoredSignatureMethods && available.length
      ? available
      : [...this.signatureMethodOptions];
    this.applySignatureMethodsToAllEntries();
  }

  private loadSignatureMethodOptions(): void {
    if (!this.cliRegId) {
      this.signatureMethodOptions = [SignatureMethodEnum.MANUAL];
      this.initSelectedSignatureMethods();
      return;
    }
    this.documentsService.getSignatureMethods(this.cliRegId).pipe(
      catchError(() => of<SignatureMethodDto[]>([])),
      tap((signatureMethods: SignatureMethodDto[]) => {
        this.signatureMethodOptions = getAvailableSignatureMethods(signatureMethods);
        this.initSelectedSignatureMethods();
        this.cd.markForCheck();
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();
  }

  private nextAvailableSignatureIndex(): number {
    const usedIndexes = new Set(this.signatureRoleEntries.map((entry) => entry.signatureIndex));
    for (let index = 0; index < MAX_SIGNATURE_ROLES; index++) {
      if (!usedIndexes.has(index)) {
        return index;
      }
    }
    return 0;
  }

  private syncSignatureRoleEntries(): void {
    const decoded = decodeSignatureData(this.signatureData);
    this.signatureOrderMode = decoded.length >= 2
      && (this.signatureData ?? []).some((entry) => (entry?.signingOrder ?? 0) > 0)
      ? 'ORDERED'
      : 'UNORDERED';
    const knownEntries = new Map(this.signatureRoleEntries.map((entry) => [entry.role, entry]));
    let index = 0;
    this.signatureRoleEntries = decoded.map((entry) => {
      if (entry.signatureIndex >= 0) {
        this.signatureIndexesByRole.set(entry.role, entry.signatureIndex);
      }
      return {
        ...entry,
        signatureIndex: this.signatureIndexesByRole.get(entry.role)
          ?? knownEntries.get(entry.role)?.signatureIndex
          ?? index++ % MAX_SIGNATURE_ROLES,
        imageDataUri: knownEntries.get(entry.role)?.imageDataUri ?? ''
      };
    });
    this.resolveSignatureIndexesFromFingerprints();
    this.seedSelectedSignatureMethods(decoded);
    this.applySignatureMethodsToAllEntries();
  }

  /**
   * Templates saved before the signature asset was persisted only carry the image fingerprints, so
   * the asset of those roles is looked up from them instead of the position in the signing order.
   */
  private resolveSignatureIndexesFromFingerprints(): void {
    this.signatureRoleEntries
      .filter((entry) => !this.signatureIndexesByRole.has(entry.role) && entry.fingerprints?.length)
      .forEach((entry) => {
        const role = entry.role;
        resolveSignatureIndexByFingerprints(role, entry.fingerprints, entry.signatureIndex)
          .then((signatureIndex: number) => {
            this.signatureIndexesByRole.set(role, signatureIndex);
            this.signatureRoleEntries = this.signatureRoleEntries.map((item) =>
              item.role === role ? {...item, signatureIndex} : item);
            this.cd.markForCheck();
          });
      });
  }

  private seedSelectedSignatureMethods(decoded: SignatureRoleEntry[]): void {
    const union = new Set<SignatureMethodEnum>();
    decoded.forEach((entry) => (entry.signatureMethods ?? []).forEach((method) => union.add(method)));
    this.hasStoredSignatureMethods = union.size > 0;
    if (union.size) {
      this.selectedSignatureMethods = this.signatureMethodOptionsLoaded
        ? sortByOptionOrder(this.signatureMethodOptions, [...union])
        : [...union];
    }
  }

  private normalizeSignatureOrderMode(): void {
    if (!this.canConfigureSigningOrder) {
      this.signatureOrderMode = 'UNORDERED';
    }
  }

  private emitSignatureData(): void {
    const encoded = encodeSignatureData(this.signatureRoleEntries);
    this.signatureDataChange.emit(this.signatureOrderMode === 'ORDERED'
      ? encoded
      : encoded.map((entry) => ({...entry, signingOrder: 0})));
  }
}
